import {
  CONTENT_ID_MANUAL_CLAIM_REQUESTS_LOADED,
  CONTENT_ID_MANUAL_CLAIM_REQUESTS_CLEAR,
  CONTENT_ID_MANUAL_CLAIM_REQUEST_DETAIL_LOADED,
  CONTENT_ID_MANUAL_CLAIM_REQUEST_DETAIL_CLEAR,
} from '../../actionTypes';
import * as api from '../../apiHelper';
import { arriveNotice } from '../../noticeActions';
import {
  setFetchLoaderLoading,
  setFetchLoaderReset,
  setSubmissionLoaderLoading,
  setSubmissionLoaderReset,
} from '../../fetchLoaderActions';

/**
 * POST /manual-claims/requests validates each item with `claim_action_type` (required).
 * Legacy or mixed payloads may use `claim_type` (Mongo field name). Joi rejects unknown
 * `claim_type`, so we merge into `claim_action_type` and omit `claim_type`.
 */
function normalizeManualClaimCreateBody(body) {
  if (!body || body.submission_type !== 'url_input' || !Array.isArray(body.items)) {
    return body;
  }
  /* eslint-disable camelcase -- request body keys match YouTube CID API (snake_case) */
  const normalized = {
    ...body,
    items: body.items.map((item) => {
      const claimAction = item.claim_action_type ?? item.claim_type;
      return {
        asset_id: item.asset_id,
        infringing_url: item.infringing_url,
        original_link: item.original_link,
        claim_action_type: claimAction,
        match_type: item.match_type || undefined,
        match_timeframe: item.match_timeframe || undefined,
        channel_link: item.channel_link || undefined,
      };
    }),
  };
  /* eslint-enable camelcase */
  return normalized;
}

function manualClaimRequestsLoaded(manualClaimRequests) {
  return {
    type: CONTENT_ID_MANUAL_CLAIM_REQUESTS_LOADED,
    payload: { manualClaimRequests },
  };
}

function manualClaimRequestsClear() {
  return { type: CONTENT_ID_MANUAL_CLAIM_REQUESTS_CLEAR };
}

function manualClaimRequestDetailLoaded(manualClaimRequestDetail) {
  return {
    type: CONTENT_ID_MANUAL_CLAIM_REQUEST_DETAIL_LOADED,
    payload: { manualClaimRequestDetail },
  };
}

function manualClaimRequestDetailClear() {
  return { type: CONTENT_ID_MANUAL_CLAIM_REQUEST_DETAIL_CLEAR };
}

async function getManualClaimRequestsApi(token, params) {
  const response = await api.get({
    path: 'youtube-cid/manual-claims/requests',
    token,
    params,
    version: 'gateway',
  });
  return response.json();
}

async function getManualClaimRequestApi(token, requestId) {
  const response = await api.get({
    path: `youtube-cid/manual-claims/requests/${requestId}`,
    token,
    version: 'gateway',
  });
  return response.json();
}

async function postManualClaimRequestApi(token, body) {
  const response = await api.post({
    path: 'youtube-cid/manual-claims/requests',
    token,
    body,
    version: 'gateway',
  });
  return response.json();
}

export function initiateFetchManualClaimRequests(params) {
  return async (dispatch, getState) => {
    const {
      auth: { token, paymentPending },
      fetchLoader: { dataLoading },
    } = getState();

    if (paymentPending) return;

    if (!dataLoading) {
      dispatch(setFetchLoaderLoading());
    }

    try {
      const manualClaimRequests = await getManualClaimRequestsApi(token, params);
      dispatch(manualClaimRequestsLoaded(manualClaimRequests));
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(arriveNotice(e.message));
      }
    } finally {
      dispatch(setFetchLoaderReset());
    }
  };
}

export function clearManualClaimRequests() {
  return (dispatch) => {
    dispatch(manualClaimRequestsClear());
  };
}

export function initiateFetchManualClaimRequestDetail(requestId) {
  return async (dispatch, getState) => {
    const {
      auth: { token, paymentPending },
      fetchLoader: { dataLoading },
    } = getState();

    if (paymentPending) return;

    if (!dataLoading) {
      dispatch(setFetchLoaderLoading());
    }

    try {
      const manualClaimRequestDetail = await getManualClaimRequestApi(token, requestId);
      dispatch(manualClaimRequestDetailLoaded(manualClaimRequestDetail));
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(arriveNotice(e.message));
      }
    } finally {
      dispatch(setFetchLoaderReset());
    }
  };
}

export function clearManualClaimRequestDetail() {
  return (dispatch) => {
    dispatch(manualClaimRequestDetailClear());
  };
}

export function initiateCreateManualClaimRequest(body) {
  return async (dispatch, getState) => {
    const {
      auth: { token, paymentPending },
      fetchLoader: { submissionLoading },
    } = getState();

    if (paymentPending) return null;

    if (!submissionLoading) {
      dispatch(setSubmissionLoaderLoading());
    }

    try {
      const payload = normalizeManualClaimCreateBody(body);
      const response = await postManualClaimRequestApi(token, payload);
      dispatch(arriveNotice(response.message || 'Request submitted.', 'ok'));
      return response;
    } catch (e) {
      if (e.name !== 'AbortError') {
        dispatch(arriveNotice(e.message));
      }
      return null;
    } finally {
      dispatch(setSubmissionLoaderReset());
    }
  };
}
